import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Points } from 'components/Points/Points';
import LoginPromptModal from 'components/LoginPromptModal/LoginPromptModal';
import { AssetUrls } from 'config/assets';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { useAddCardToHaveListMutation } from 'store/services/collectionList/haveList/haveListApi';
import { useLazyGetCardHoldersQuery } from 'store/services/cards/holders/cardHoldersApi';
import { CardCondition } from 'types/interfaces/condition/condition';
import { showCardAddedToHaveListSuccess, showError, showFinishAdjustment } from 'utils/toastNotifications';
import { writeOfferBuilderSeed } from 'utils/offerBuilderSeed';
import CounterpartyPickerModal from './CounterpartyPickerModal';
import { HolderRow, groupHoldersByUser, holderToSeed } from './counterpartyPicker';
import './CardActionMenu.css';
import { finishFields } from 'utils/finishes';

/** Fixed at every card size — the menu is a constant, the card is not. */
const MENU_WIDTH = 224;
/** The menu overlaps its own card by 4px rather than clearing it: sitting fully
 *  clear pushes it into the neighbouring column and reads as that card's menu. */
const OVERLAP = 4;
const EDGE_PADDING = 12;
/** Header (38) + 4px margin + 4 rows x 38 + 2 x 6px padding. Only an estimate
 *  for the flip decision — keep it in step with the row count. */
const MENU_HEIGHT_ESTIMATE = 206;

export interface CardActionMenuCard {
  name: string;
  /** Numeric DB id — what the have-list endpoint expects, NOT a Scryfall UUID. */
  id: number;
  scryfallId: string;
  setCode?: string;
  collectorNumber?: string;
  points?: number | null;
}

interface CardActionMenuProps {
  card: CardActionMenuCard;
  /** Rect of the card that opened the menu, for anchoring. */
  anchorRect: DOMRect | null;
  isMobile: boolean;
  onClose: () => void;
  /** Element focus returns to when the menu closes on Escape. */
  returnFocusTo?: HTMLElement | null;
  /** Eligible users offering this printing for trade. undefined until the backend sends it. */
  offeredCount?: number;
}

interface MenuAction {
  key: string;
  label: string;
  icon: string;
  /** Requires a session; gated at click time with LoginPromptModal. */
  gated: boolean;
  emphasis?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  /**
   * The action closes the menu itself. Set by rows whose work is async: the
   * shared `onClose()` after `run()` would otherwise fire while the request is
   * still in flight and unmount whatever the row was about to open.
   */
  selfClosing?: boolean;
  run: () => void;
}

/** Anchor to the card, flipping to stay inside the viewport.
 *  Same shape as calculatePosition in hooks/useHoverCard.ts. */
const calculatePosition = (rect: DOMRect) => {
  const { innerWidth, innerHeight } = window;

  // Left edge sits 4px inside the card's right edge, so the menu reads as
  // belonging to the card it covers rather than to the one beside it.
  let left = rect.right - OVERLAP;
  let top = rect.top;

  // Flip to the card's left when the right side would overflow. Mirrored, so
  // the menu's right edge sits 4px inside the card's left edge.
  if (left + MENU_WIDTH > innerWidth - EDGE_PADDING) {
    left = rect.left + OVERLAP - MENU_WIDTH;
  }
  // Both sides overflow (a very narrow window): clamp inside the right edge.
  if (left < EDGE_PADDING) {
    left = Math.max(EDGE_PADDING, innerWidth - MENU_WIDTH - EDGE_PADDING);
  }

  // Lift the menu when it would run off the bottom.
  if (top + MENU_HEIGHT_ESTIMATE > innerHeight - EDGE_PADDING) {
    top = innerHeight - MENU_HEIGHT_ESTIMATE - EDGE_PADDING;
  }
  if (top < EDGE_PADDING) {
    top = EDGE_PADDING;
  }

  return { left, top };
};

/**
 * The six actions available on a search result.
 *
 * One component, two presentations: an anchored popover on pointer devices and
 * a bottom sheet on touch. The action array is built once so the two cannot
 * drift apart.
 */
export const CardActionMenu: React.FC<CardActionMenuProps> = ({
  card,
  anchorRect,
  isMobile,
  onClose,
  returnFocusTo,
  offeredCount,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [addCardToHaveList] = useAddCardToHaveListMutation();
  const [fetchHolders, { isFetching: isLoadingHolders }] = useLazyGetCardHoldersQuery();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [picker, setPicker] = useState<{
    rows: HolderRow[];
    totalHolders: number;
    totalAvailableToTrade: number;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Every destination carries the return path so the user lands back in the
  // exact result set they left.
  const navState = useMemo(
    () => ({
      from: 'search',
      returnPath: window.location.pathname + window.location.search,
    }),
    [],
  );

  const encodedName = encodeURIComponent(card.name);
  const printingsUrl = `/view-card-printings/${encodedName}?cardId=${card.scryfallId}`;

  const handleAddToHave = useCallback(async () => {
    try {
      // The backend stores the finish the printing exists in and reports the
      // correction (D-7); a quick add asks for non-foil and defers to that.
      const created = await addCardToHaveList({
        cardId: card.id,
        quantity: 1,
        condition: CardCondition.NEAR_MINT,
        ...finishFields('NONFOIL'),
        source: 'search',
      }).unwrap();
      showCardAddedToHaveListSuccess(card.name, 1);
      if (created?.adjustment) showFinishAdjustment(created.adjustment);
    } catch {
      showError('Failed to add card to your Have List. Please try again.');
    }
  }, [addCardToHaveList, card.id, card.name]);

  /** Seed the builder with this holder's copy and hand off to the shipped route. */
  const openBuilderWith = useCallback(
    (holder: Parameters<typeof holderToSeed>[0]) => {
      writeOfferBuilderSeed(holderToSeed(holder));
      // Bare navigate, matching all three shipped seed callers: the handoff
      // rides entirely on sessionStorage, and this route is behind
      // ProtectedRoute, whose login redirect would drop any location state.
      navigate(`/trade-offers/new/${holder.username}`);
    },
    [navigate],
  );

  /**
   * Resolve which person to build the offer with.
   *
   * Branches on the GROUPED row count, never on `offeredCount`. The badge is now
   * viewer-aware server-side (it excludes the viewer's own copies and blocked
   * users, sharing one expression with this endpoint), so the two agree on those.
   * One divergence remains by design: the badge counts copies the picker drops as
   * unpriceable, because pricing every row of every search page is the cost that
   * was deliberately removed from that hot path. Scheduled for closure once the
   * precomputed price signal lands. `offeredCount` keeps only its zero-case job,
   * which needs no request at all.
   */
  const handleMakeOffer = useCallback(async () => {
    if (isLoadingHolders) return; // double-fire guard
    try {
      const res = await fetchHolders({ cardId: card.id, limit: 100 }, true).unwrap();
      const rows = groupHoldersByUser(res.holders ?? []);

      if (rows.length === 0) {
        // Phrased as a PAIRING failure, not a data failure: a viewer-independent
        // count can never promise the viewer anything, so an empty picker is an
        // expected outcome rather than an error. Covers self-holding, blocks, and
        // the priceability residual with one sentence.
        showError('Nobody is available to trade this with you right now.');
        onClose();
        return;
      }

      if (rows.length === 1) {
        // One person: seed their best copy and skip the picker entirely. A lone
        // holder with several copies still lands here — grouping already chose.
        openBuilderWith(rows[0].holder);
        onClose();
        return;
      }

      setPicker({
        rows,
        totalHolders: res.totalHolders,
        totalAvailableToTrade: res.totalAvailableToTrade,
      });
    } catch {
      showError('Could not load who has this card. Please try again.');
      onClose();
    }
  }, [fetchHolders, card.id, isLoadingHolders, onClose, openBuilderWith]);

  const actions = useMemo<MenuAction[]>(() => {
    // Nobody offering is the one sanctioned disabled row: there is nobody to
    // make an offer to, and the want-list row below it stays enabled. Strict
    // === 0 on purpose — an absent count means "not shipped yet", not "zero",
    // and must leave the row enabled rather than falsely disabling it.
    const noOfferers = offeredCount === 0;
    return [
      {
        key: 'want',
        label: 'Add to want list',
        icon: AssetUrls.icons.misc.heart,
        gated: true,
        // The printings picker IS the want flow: it is where set, foil, language
        // and condition preferences get chosen before the want row is written.
        run: () => navigate(printingsUrl, { state: navState }),
      },
      {
        key: 'have',
        label: 'Add to have list',
        icon: AssetUrls.icons.misc.have,
        gated: true,
        run: handleAddToHave,
      },
      {
        key: 'offer',
        label: isLoadingHolders ? 'Loading…' : 'Make an offer',
        icon: AssetUrls.icons.misc.offer,
        gated: true,
        emphasis: true,
        disabled: noOfferers,
        disabledReason: 'Nobody has this available yet',
        // Async: closes itself on every branch, so runAction must not also close
        // and unmount the picker the moment it is set.
        selfClosing: true,
        run: handleMakeOffer,
      },
      {
        key: 'send',
        label: 'Send this card',
        icon: AssetUrls.icons.navigation.sending,
        gated: true,
        run: () =>
          navigate(`/sendcards?searchType=card&search=${encodedName}`, { state: navState }),
      },
    ];
  }, [
    offeredCount,
    navigate,
    navState,
    encodedName,
    printingsUrl,
    handleAddToHave,
    handleMakeOffer,
    isLoadingHolders,
  ]);

  // Card-detail target, now carried by the menu's header row rather than a
  // list row. A real <Link> so Cmd/Ctrl-click and middle-click open a new tab.
  const cardDetailUrl = buildCardUrl({
    name: card.name,
    setCode: card.setCode,
    collectorNumber: card.collectorNumber,
    scryfallID: card.scryfallId,
  });

  const runAction = useCallback(
    (action: MenuAction) => {
      if (action.disabled) return;
      if (action.gated && !currentUser) {
        setShowLoginModal(true);
        return;
      }
      action.run();
      if (!action.selfClosing) onClose();
    },
    [currentUser, onClose],
  );

  // Close on route change: the menu anchors to a card the next route will not
  // render. Compared on pathname alone — /search rewrites its own query string
  // on every keystroke, and that is not a navigation.
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== pathRef.current) {
      pathRef.current = location.pathname;
      onClose();
    }
  }, [location.pathname, onClose]);

  // Back/forward buttons bypass the router's location update in some flows, so
  // listen for the browser event too.
  useEffect(() => {
    const onPopState = () => onClose();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [onClose]);

  // Escape returns focus to the card that opened the menu; every other close
  // path leaves focus where the user put it.
  const closeAndRestore = useCallback(() => {
    onClose();
    returnFocusTo?.focus();
  }, [onClose, returnFocusTo]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // The picker is a modal dialog on top of this menu: it owns Escape and the
      // arrow keys while it is open, or both would move at once.
      if (showLoginModal || picker) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeAndRestore();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((prev) => {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          return (prev + step + actions.length) % actions.length;
        });
        return;
      }

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        setActiveIndex(event.key === 'Home' ? 0 : actions.length - 1);
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const action = actions[activeIndex];
        if (action) runAction(action);
      }

      // No single-letter shortcuts: SearchFromAnywhere is mounted app-wide and
      // opens its overlay on ANY printable character (SearchFromAnywhere.tsx:144),
      // so a letter here would both fire the row and open global search.
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [actions, activeIndex, runAction, closeAndRestore, showLoginModal, picker]);

  // Focus trap: keep focus on the active row so Tab cannot escape the menu.
  // Skipped while the picker is open — it manages its own row focus, and
  // stealing it back would fight the dialog for the caret.
  useEffect(() => {
    if (picker) return;
    rowRefs.current[activeIndex]?.focus();
  }, [activeIndex, picker]);

  // Outside click, and scroll of the results container.
  useEffect(() => {
    // While the picker is open, a click inside it is outside the MENU — closing
    // here would tear it down mid-selection. The picker owns its own dismissal.
    if (showLoginModal || picker) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current || menuRef.current.contains(event.target as Node)) return;
      // A mousedown on another card is that card's job to handle: closing here
      // would swallow the click and the user would have to press twice to move
      // the menu. The grid keeps one menu open, so the handoff is safe.
      if ((event.target as HTMLElement).closest?.('.result-card')) return;
      onClose();
    };
    const onScroll = () => onClose();

    // Deferred: the click that opened the menu is still propagating.
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      window.addEventListener('scroll', onScroll, true);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose, showLoginModal, picker]);

  const rows = actions.map((action, index) => (
    <button
      key={action.key}
      ref={(el) => {
        rowRefs.current[index] = el;
      }}
      type="button"
      role="menuitem"
      /* `card-action-row-emphasis`, not a bare `emphasis`: articles.css:650
         declares a global `.emphasis` (2rem margins, a border-left and its own
         background) and all page CSS is global, so the short name turned this
         row into a separated block. */
      className={`card-action-row ${action.emphasis ? 'card-action-row-emphasis' : ''} ${
        index === activeIndex ? 'active' : ''
      }`}
      disabled={action.disabled}
      title={action.disabled ? action.disabledReason : undefined}
      aria-disabled={action.disabled}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={(e) => {
        e.stopPropagation();
        runAction(action);
      }}
    >
      <img className="card-action-icon" src={action.icon} alt="" aria-hidden="true" />
      <span className="card-action-label">{action.label}</span>
    </button>
  ));

  // Card name + diagonal arrow. The whole row is the link to card details —
  // stopPropagation keeps the menu's own handlers out of it, and we do NOT
  // preventDefault so the href drives navigation and modifier-clicks work.
  const header = (
    <Link
      className="card-action-header"
      to={cardDetailUrl}
      state={navState}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="card-action-header-name">{card.name}</span>
      <img
        className="card-action-header-arrow"
        src={AssetUrls.search.arrowDiagonal}
        alt="View details"
      />
    </Link>
  );

  const loginModal = (
    <LoginPromptModal
      isOpen={showLoginModal}
      onClose={() => {
        setShowLoginModal(false);
        onClose();
      }}
      collectionOwnerUsername={card.name}
    />
  );

  // Only rendered for two or more holders; zero and one never reach here.
  const counterpartyPicker = picker ? (
    <CounterpartyPickerModal
      rows={picker.rows}
      totalHolders={picker.totalHolders}
      totalAvailableToTrade={picker.totalAvailableToTrade}
      card={{
        name: card.name,
        setName: picker.rows[0]?.holder.card.setName,
        setCode: card.setCode,
        collectorNumber: card.collectorNumber,
        imageUrl: picker.rows[0]?.holder.card.imageUrl,
      }}
      isMobile={isMobile}
      onSelect={(row) => {
        openBuilderWith(row.holder);
        setPicker(null);
        onClose();
      }}
      onClose={() => {
        setPicker(null);
        onClose();
      }}
      returnFocusTo={returnFocusTo}
    />
  ) : null;

  if (isMobile) {
    // Bottom sheet — same shell as MobileFilterSheet, same action array.
    return createPortal(
      <>
        <div className="card-action-sheet-overlay" onClick={onClose} aria-hidden="true" />
        <div
          className="card-action-sheet"
          role="menu"
          aria-label={`Actions for ${card.name}`}
          ref={menuRef}
        >
          <button
            type="button"
            className="card-action-sheet-handle"
            onClick={onClose}
            aria-label="Close actions"
          >
            <span className="card-action-sheet-handle-bar" />
          </button>

          <div className="card-action-sheet-header">
            <span className="card-action-sheet-name">{card.name}</span>
            {card.points != null && <Points type="small" points={card.points} />}
          </div>

          <div className="card-action-sheet-rows">{rows}</div>

          <button type="button" className="card-action-sheet-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
        {loginModal}
        {counterpartyPicker}
      </>,
      document.body,
    );
  }

  const position = anchorRect ? calculatePosition(anchorRect) : { left: -9999, top: -9999 };

  // Portalled: the results grid and the glass panel both clip their overflow.
  return createPortal(
    <>
      <div
        ref={menuRef}
        className="card-action-menu"
        role="menu"
        aria-label={`Actions for ${card.name}`}
        style={{ left: position.left, top: position.top, width: MENU_WIDTH }}
        onClick={(e) => e.stopPropagation()}
      >
        {header}
        {rows}
      </div>
      {loginModal}
      {counterpartyPicker}
    </>,
    document.body,
  );
};

export default CardActionMenu;
